
// #Funtion to limit max length of the string at 2000 symbols
import {DefaultPropertyConfig} from "@app/core-app/app/data/profile";
import logger from "@app/core-app/logger";

const trimLongString = (string: string, limit: number): string => {
  if (string.length > limit - 3 - 1) {
    return string.substring(0, limit) + "..."
  }
  return string
}

export class NotionPropertiesService{

  static supportedDefaultFields = [
    "rich_text",
    "select",
    "multi_select",
    "date",
    "email",
    "title",
    "url",
    "checkbox",
    "number",
    "phone_number",
    "status"
  ]

  static supportedUpdateFields = NotionPropertiesService.supportedDefaultFields

  static createUpdateForDefaultProperty(property: DefaultPropertyConfig): object | null {
    switch (property.type){
    case "rich_text":
      return NotionPropertiesService.createTextPropertiesUpdate({}, property.name, property.defaultValue)
    case "select":
      return NotionPropertiesService.createSelectPropertiesUpdate({}, property.name, property.defaultValue)
    case "multi_select":
      return NotionPropertiesService.createMultiselectPropertiesUpdate({}, property.name, property.defaultValue)
    case "date":
      return NotionPropertiesService.createDatePropertiesUpdate({}, property.name, property.defaultValue)
    case "email":
      return NotionPropertiesService.createEmailPropertiesUpdate({}, property.name, property.defaultValue)
    case "title":
      return NotionPropertiesService.createTitlePropertiesUpdate({}, property.name, property.defaultValue)
    case "url":
      return NotionPropertiesService.createUrlPropertiesUpdate({}, property.name, property.defaultValue)
    case "checkbox":
      return NotionPropertiesService.createCheckboxPropertiesUpdate({}, property.name, property.defaultValue)
    case "number":
      return NotionPropertiesService.createNumberPropertiesUpdate({}, property.name, property.defaultValue)
    case "phone_number":
      return NotionPropertiesService.createPhoneNumberPropertiesUpdate({}, property.name, property.defaultValue)
    case "status":
      return NotionPropertiesService.createStatusPropertiesUpdate({}, property.name, property.defaultValue)
    default:
      return null
    }

  }

  static createUpdateForExtractedProperty(property: {name: string, value: any, type: string}): object | null {
    switch (property.type){
    case "rich_text":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: Rich Text: property.value is not string")
        return null
      }
      return NotionPropertiesService.createTextPropertiesUpdate({}, property.name, property.value)
    case "select":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createSelectPropertiesUpdate({}, property.name, property.value)
    case "multi_select":
      if (!Array.isArray(property.value)) {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not array")
        return null
      }
      return NotionPropertiesService.createMultiselectPropertiesUpdate({}, property.name, property.value)
    case "date":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createDatePropertiesUpdate({}, property.name, property.value)
    case "email":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createEmailPropertiesUpdate({}, property.name, property.value)
    case "title":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createTitlePropertiesUpdate({}, property.name, property.value)
    case "url":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createUrlPropertiesUpdate({}, property.name, property.value)
    case "checkbox":
      if (typeof property.value === "string" || typeof property.value === "boolean") {
        return NotionPropertiesService.createCheckboxPropertiesUpdate({}, property.name, property.value)
      }
      return null
    case "number":
      if (typeof property.value != "number" && typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createNumberPropertiesUpdate({}, property.name, property.value)
    case "phone_number":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createPhoneNumberPropertiesUpdate({}, property.name, property.value)
    case "status":
      if (typeof property.value != "string") {
        // logger.error("NotionPropertiesService: createUpdateForExtractedProperty: property.value is not string")
        return null
      }
      return NotionPropertiesService.createStatusPropertiesUpdate({}, property.name, property.value)
    default:
      return null
    }

  }

  static createStatusPropertiesUpdate(properties: object, property: string, value: any): object {
    let objectValue = typeof value === "object" ? value.value : null

    return Object.assign(properties, {
      [property]: {
        "status": {
          "name": objectValue
        }
      }
    })
  }

  static createCheckboxPropertiesUpdate(properties: object, property: string, value: string | boolean): object {
    const stringValue = typeof value === "string" ? value : value.toString()
    return Object.assign(properties, {
      [property]: {
        type: "checkbox",
        checkbox: stringValue === "true"
      }
    })
  }

  static createNumberPropertiesUpdate(properties: object, property: string, value: string | number): object {
    let numberValue = null
    if (value) {
      numberValue = Number(value)
      if (Number.isNaN(numberValue)) {
        numberValue = null
      }
    }

    return Object.assign(properties, {
      [property]: {
        number: numberValue
      }
    })
  }

  static createPhoneNumberPropertiesUpdate(properties: object, property: string, value: string): object {
    return Object.assign(properties, {
      [property]: {
        phone_number: value
      }
    })
  }
  static createUrlPropertiesUpdate(properties: object, property: string, value: string): object {
    return Object.assign(properties, {
      [property]: {
        type: "url",
        url: value
      }
    })
  }

  static createSelectPropertiesUpdate(properties: object, property: string, selectLabel: any): object {
    let objectValue = typeof selectLabel === "object" ? selectLabel.value : null
    return Object.assign(properties, {
      [property]: {
        type: "select",
        select: {
          name: objectValue ?? selectLabel
        }
      }
    })
  }

  static createMultiselectPropertiesUpdate(properties: object, property: string, selectLabels: any[]): object {
    let values = selectLabels.map(label => typeof label === "object" ? label.value : label)

    return Object.assign(properties, {
      [property]: {
        type: "multi_select",
        multi_select: values.map(value => ({name: value}))
      }
    })
  }

  static createTextPropertiesUpdate(properties: object, property: string, value: string): object {
    return Object.assign(properties, {
      [property]: {
        type: "rich_text",
        rich_text: [{text: {content: value}}]
      }
    })
  }

  static createDatePropertiesUpdate(properties: object, property: string, value: string): object {
    return Object.assign(properties, {
      [property]: {
        type: "date",
        date: {start: value}
      }
    })
  }

  static createEmailPropertiesUpdate (properties: object, property: string, value: string): object {
    return Object.assign(properties, {
      [property]: {
        type: "email",
        email: value
      }
    })
  }

  static createRelationPropertiesUpdate (properties: object, property: string, values: string[]): object {
    return Object.assign(properties, {
      [property]: {
        type: "relation",
        relation: values.map(value => ({id: value}))
      }
    })
  }

  static createTitlePropertiesUpdate(properties: object, property: string, value: string): object {
    return Object.assign(properties, {
      [property]: {
        title: [
          {
            text: {
              content: trimLongString(value, 100)
            }
          }
        ]
      }
    })
  }

  static createFilePropertiesUpdate(properties: object, property: string, files: object[]): object {
    return Object.assign(properties, {
      [property]: {
        files: files
      }
    })
  }
}